import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {ModalDetails} from '@Components/modal/modal-context';
import {
  digitalSignageFlow,
  setErrorMessageForPublishing,
  setErrorOnPublishPanel,
  setHashedId,
  shareToAppFlow,
  socialMediaFlow,
  webpageEmbedFlow,
  webpagePublishFlow,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {DisplayType, ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {GA4EventParamName} from '@Libraries/ga-events';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {noop} from '@Utils/general.util';
import styles from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.module.scss';
import {openDownloadModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {useHashedId} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {PublishingOptions, PublishingOptionsDefaultListProps} from './publishing-options-default-list.types';
import {useAppDispatch} from '@/hooks';
import {getEventWizardURLWithDesignPrefill, isEventsFeatureEnabled} from '@Libraries/events.library';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {getPosterPreviewURL} from '@Libraries/poster-library';
import {NewFeatureTag} from '@Components/new-feature-tag';

export function PublishingOptionsDefaultList({...props}: PublishingOptionsDefaultListProps): ReactElement {
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const {displayType} = props;
  const ANALYTICS_ACTION_FOR_PUBLISH = 'ChoosePublishType';
  const dispatch = useAppDispatch();
  const hashedId = useHashedId();

  const handleDownloadClick = (posterHashedId?: string): void => {
    if (posterHashedId) {
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'Download'});
      openDownloadModal(undefined, '', posterHashedId);
      props.closePanel();
    }
  };

  const startShareToAppFlow = (): void => {
    dispatch(shareToAppFlow());
  };
  const startDigitalSignageFlow = (): void => {
    dispatch(digitalSignageFlow());
  };

  const startWebpagePublishFlow = (): void => {
    dispatch(webpagePublishFlow());
  };

  const startWebpageEmbedFlow = (): void => {
    dispatch(webpageEmbedFlow());
  };
  const startSocialMediaFlow = (): void => {
    dispatch(socialMediaFlow());
  };

  const handleSocialMediaClick = (posterHashedId?: string): void => {
    if (posterHashedId) {
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'SocialMediaPublishing'});
      window.location.href = window.PMW.util.site_url(`socialmedia/post/design/${posterHashedId}`);
    }
  };

  const handleEventCreationClick = (posterHashedId?: string): void => {
    if (!posterHashedId) {
      return;
    }
    window.location.href = getEventWizardURLWithDesignPrefill(posterHashedId, getPosterPreviewURL(posterHashedId), MediaTypes.MY_DESIGNS);
  };

  const handleEmailCampaignClick = (posterHashedId?: string): void => {
    if (posterHashedId) {
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'EmailCampaign'});
      if (window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG) {
        void window.PMW.closeModalDialog('download-publish-dialog').then(() => {
          void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN);
        });
      } else {
        window.location.href = window.PMW.util.site_url(`emailmarketing/publish/design/${posterHashedId}`);
      }
    }
  };

  const publishingOptionsIcons: PublishingOptions[] = [
    {
      id: 1,
      iconName: 'icon-social-media',
      heading: window.i18next.t('pmwjs_social_media'),
      subheading: window.i18next.t('pmwjs_social_media_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: handleSocialMediaClick,
    },
    ...(isEventsFeatureEnabled()
      ? [
          {
            id: 9,
            iconName: 'icon-event',
            heading: window.i18next.t('pmwjs_promote_as_event'),
            subheading: window.i18next.t('pmwjs_create_event_design_automatically'),
            thumbNailType: ThumbNailType.ICON,
            onClick: handleEventCreationClick,
            afterHeadingTextContent: <NewFeatureTag className="radius-round spacing-m-l-2" />,
          },
        ]
      : []),
    {
      id: 2,
      iconName: 'icon-envelope',
      heading: window.i18next.t('pmwjs_email_campaign_new'),
      subheading: window.i18next.t('pmwjs_email_campaign_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: handleEmailCampaignClick,
    },
    {
      id: 3,
      iconName: 'icon-download',
      heading: window.i18next.t('pmwjs_download'),
      subheading: window.i18next.t('pmwjs_download_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: handleDownloadClick,
    },
    {
      id: 4,
      iconName: 'icon-link',
      heading: window.i18next.t('pmwjs_publish_webpage_new'),
      subheading: window.i18next.t('pmwjs_publish_webpage_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: startWebpagePublishFlow,
    },
    {
      id: 5,
      iconName: 'icon-code',
      heading: window.i18next.t('pmwjs_embed_in_webpage'),
      subheading: window.i18next.t('pmwjs_embed_in_webpage_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: startWebpageEmbedFlow,
    },
    {
      id: 6,
      iconName: 'icon-tv',
      heading: window.i18next.t('pmwjs_digital_signage_new'),
      subheading: window.i18next.t('pmwjs_digital_signage_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: startDigitalSignageFlow,
    },
    {
      id: 7,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_share_link_social'),
      subheading: window.i18next.t('pmwjs_link_on_social_media_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: startSocialMediaFlow,
    },
  ];

  if (inMobile) {
    const shareToApp: PublishingOptions = {
      id: 8,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_share_to_app'),
      subheading: window.i18next.t('pmwjs_share_to_app_desc'),
      thumbNailType: ThumbNailType.ICON,
      onClick: startShareToAppFlow,
    };
    publishingOptionsIcons.splice(3, 0, shareToApp);
  }

  const onSaveSuccess = (onClickFunction: (posterHashedId?: string) => void): void => {
    const posterHashedId = window.posterEditor?.whiteboard?.hashedID || '';
    dispatch(setHashedId(posterHashedId));

    if (onClickFunction) {
      onClickFunction(posterHashedId);
    }
  };

  const onOptionClick = (onClickFunction: (posterHashedId?: string) => void): void => {
    if (window.PMW.isReactPosterMaker()) {
      window?.posterEditor?.whiteboard?.savePoster
        .save()
        .then(() => {
          onSaveSuccess(onClickFunction);
        })
        .catch(() => {
          dispatch(setErrorOnPublishPanel(true));
          dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
        });
    } else {
      onClickFunction(hashedId);
    }
  };

  let panelStyle: string;
  if (inMobile) {
    panelStyle = styles.publishingOptionsGapPanelMobile;
  } else {
    panelStyle = displayType === DisplayType.MODAL ? styles.publishingOptionsGapPanel : styles.publishingOptionsGapDropDown;
  }

  return (
    <div className={panelStyle}>
      {publishingOptionsIcons.map((option: PublishingOptions) => {
        return (
          <PublishingOptionListItem
            key={option.id}
            customThumbnailClasses="bg-primary-10 content-primary"
            iconName={option.iconName}
            heading={option.heading}
            afterHeadingTextContent={option.afterHeadingTextContent}
            subheading={option.subheading}
            thumbNailType={ThumbNailType.ICON}
            onClick={() => {
              onOptionClick(option.onClick ?? noop);
            }}
          />
        );
      })}
    </div>
  );
}
